import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Divider,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  NotificationsActive,
  CheckCircle,
  Delete,
  DoneAll,
  LocalShipping,
  AttachMoney,
  Description,
  Info,
} from "@mui/icons-material";
import { notificationAPI } from "../services/api";

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchNotifications();
  }, [navigate]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await notificationAPI.getNotifications(1, 50);
      if (response.data.success) {
        setNotifications(response.data.data.notifications || []);
      }
    } catch (err) {
      setError("Failed to fetch notifications");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationAPI.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationAPI.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Failed to delete notification:", err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      notificationAPI.markAsRead(notification.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
      );
    }

    switch (notification.reference_type) {
      case "quote":
        navigate("/quotes");
        break;
      case "order":
        navigate("/orders");
        break;
      case "finance":
        navigate("/finance");
        break;
      case "tender":
        navigate("/tenders");
        break;
      default:
        break;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "quote_received":
        return <AttachMoney color="success" />;
      case "order_confirmed":
      case "order_dispatched":
        return <LocalShipping color="primary" />;
      case "finance_approved":
        return <CheckCircle color="success" />;
      case "finance_rejected":
        return <Info color="error" />;
      case "tender_uploaded":
        return <Description color="info" />;
      default:
        return <NotificationsActive color="action" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const filteredNotifications =
    activeTab === 0
      ? notifications
      : activeTab === 1
      ? notifications.filter((n) => !n.is_read)
      : notifications.filter((n) => n.is_read);

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <Container maxWidth="lg">
        <Box py={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box>
              <Typography variant="h4" gutterBottom>
                Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {unreadCount > 0
                  ? `You have ${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
                  : "All caught up!"}
              </Typography>
            </Box>
            {unreadCount > 0 && (
              <Button
                variant="outlined"
                startIcon={<DoneAll />}
                onClick={handleMarkAllAsRead}
              >
                Mark All Read
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ mb: 3 }}
          >
            <Tab label={`All (${notifications.length})`} />
            <Tab label={`Unread (${unreadCount})`} />
            <Tab label={`Read (${notifications.length - unreadCount})`} />
          </Tabs>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <NotificationsActive
                sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                {activeTab === 1 ? "No unread notifications" : "No notifications yet"}
              </Typography>
            </Paper>
          ) : (
            <Card>
              {filteredNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  {index > 0 && <Divider />}
                  <CardContent
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: !notification.is_read
                        ? "action.hover"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "action.selected",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Box sx={{ mt: 0.5 }}>
                        {getNotificationIcon(notification.type)}
                      </Box>
                      <Box flex={1}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Typography variant="body1">
                              {notification.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTime(notification.created_at)}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            {!notification.is_read && (
                              <Chip
                                label="New"
                                size="small"
                                color="primary"
                                sx={{ height: 20 }}
                              />
                            )}
                            {!notification.is_read && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleMarkAsRead(notification.id, e)}
                                title="Mark as read"
                              >
                                <CheckCircle
                                  fontSize="small"
                                  color="success"
                                />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => handleDelete(notification.id, e)}
                              title="Delete"
                            >
                              <Delete fontSize="small" color="error" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </React.Fragment>
              ))}
            </Card>
          )}
        </Box>
      </Container>
    </div>
  );
};

export default Notifications;
